import './Dashboard.scss'

import { useEffect, useMemo, useState } from 'react'
import DashboardService from '../../services/dashboard.service'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { filterParticipantsEstimativeSchema } from '../../schemas/dashboardSchema'
import { formatDate } from '../../shared/utils/formatDate'
import StatisticsCard from './components/StatisticsCard/StatisticsCard'
import { Line, Pie } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js'
import ReactExport from 'react-data-export'
import { formatAttendanceData } from './utils/formatExcelData'
const ExcelFile = ReactExport.ExcelFile
const ExcelSheet = ReactExport.ExcelFile.ExcelSheet

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
)

const Dashboard = () => {
  const [participantsEstimative, setParticipantsEstimative] = useState(null)

  const currentYear = new Date().getFullYear()
  const startDate = useMemo(() => new Date(currentYear, 0, 1), [currentYear])
  const endDate = useMemo(() => new Date(currentYear, 11, 31), [currentYear])

  const getMonthName = (monthNumber) => {
    const months = [
      'Janeiro',
      'Fevereiro',
      'Março',
      'Abril',
      'Maio',
      'Junho',
      'Julho',
      'Agosto',
      'Setembro',
      'Outubro',
      'Novembro',
      'Dezembro'
    ]
    return months[monthNumber - 1]
  }

  const lineChartData = participantsEstimative
    ? {
        labels: participantsEstimative.monthlyAttendance.map(
          (item) => `${getMonthName(item.month)}/${item.year}`
        ),
        datasets: [
          {
            label: 'Atendimentos no Mês',
            data: participantsEstimative.monthlyAttendance.map(
              (item) => item.count
            ),
            borderColor: 'rgb(75, 192, 192)',
            backgroundColor: 'rgba(75, 192, 192, 0.5)',
            tension: 0.1
          }
        ]
      }
    : null

  const lineChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top'
      },
      title: {
        display: false
      }
    },
    scales: {
      y: {
        beginAtZero: true
      }
    }
  }

  const pieChartData = participantsEstimative
    ? {
        labels: participantsEstimative.rangeDistribution.map((item) => {
          if (item.range === 'other') {
            return 'Outro'
          }

          return item.range
        }),
        datasets: [
          {
            label: 'Distribuição por Faixa',
            data: participantsEstimative.rangeDistribution.map(
              (item) => item.count
            ),
            backgroundColor: [
              'rgba(255, 99, 132, 0.6)',
              'rgba(54, 162, 235, 0.6)',
              'rgba(255, 206, 86, 0.6)',
              'rgba(75, 192, 192, 0.6)',
              'rgba(153, 102, 255, 0.6)'
            ],
            borderColor: [
              'rgba(255, 99, 132, 1)',
              'rgba(54, 162, 235, 1)',
              'rgba(255, 206, 86, 1)',
              'rgba(75, 192, 192, 1)',
              'rgba(153, 102, 255, 1)'
            ],
            borderWidth: 1
          }
        ]
      }
    : null

  const pieChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top'
      }
    }
  }

  useEffect(() => {
    async function loadBooks() {
      const loadedParticipantsEstimative =
        await DashboardService.participantsEstimative(
          formatDate(startDate, 'yyyy-MM-dd'),
          formatDate(endDate, 'yyyy-MM-dd')
        )
      setParticipantsEstimative(loadedParticipantsEstimative)
    }

    loadBooks()
  }, [startDate, endDate])

  const { register, handleSubmit, formState } = useForm({
    resolver: zodResolver(filterParticipantsEstimativeSchema),
    defaultValues: {
      startDate: formatDate(startDate, 'yyyy-MM-dd'),
      endDate: formatDate(endDate, 'yyyy-MM-dd')
    }
  })

  async function handleFilter(data) {
    const filteredParticipantsEstimative =
      await DashboardService.participantsEstimative(
        formatDate(data.startDate, 'yyyy-MM-dd'),
        formatDate(data.endDate, 'yyyy-MM-dd')
      )
    setParticipantsEstimative(filteredParticipantsEstimative)
  }

  const formattedAttendanceData = participantsEstimative
    ? formatAttendanceData(participantsEstimative)
    : {}

  return (
    <div className='content-card'>
      <div className='row page-title'>
        <h1>Dashboard</h1>
      </div>
      <div className='row estimative-page-container'>
        <div className='estimative-filter'>
          <div className='estimative-filter-header'>
            <i className='fas fa-filter'></i>
            <h4 className='form-title'>Filtro</h4>
          </div>
          <form
            className='estimative-filter-form'
            onSubmit={handleSubmit(handleFilter)}
          >
            <fieldset>
              <label htmlFor='filter-start-date'>A partir de:</label>
              <input
                type='date'
                className='form-control'
                id='filter-start-date'
                {...register('startDate')}
              />
            </fieldset>
            <fieldset>
              <label htmlFor='filter-start-date'>Até:</label>
              <input
                type='date'
                className='form-control'
                id='filter-end-date'
                {...register('endDate')}
              />
            </fieldset>
            <button
              type='submit'
              className='button-full btn-save'
              disabled={formState.isSubmitting}
            >
              {formState.isSubmitting ? (
                <i className='fas fa-spinner spinner-animation'></i>
              ) : (
                'Filtrar'
              )}
            </button>
            <ExcelFile
              element={
                <button
                  type='button'
                  className='button-full btn-export'
                >
                  {' '}
                  <i className='fa fa-file-excel'></i> Exportar
                </button>
              }
              filename={'estimativa_de_atendimentos'}
            >
              <ExcelSheet
                name='Estimativa de Atendimentos'
                dataSet={formattedAttendanceData.estimativeDataset}
              ></ExcelSheet>
              <ExcelSheet
                name='Atendimentos por mês'
                dataSet={formattedAttendanceData.monthlyAttendanceDataset}
              ></ExcelSheet>
            </ExcelFile>
          </form>
        </div>

        <div className='estimative-charts-container'>
          <div className='estimative-numbers-container'>
            <StatisticsCard
              title='Instrutores Ativos'
              iconClasses='fas fa-user'
              statistic={participantsEstimative?.activeInstructors}
            />

            <StatisticsCard
              title='Estimativa de Atendimentos'
              iconClasses='fas fa-bullseye'
              statistic={participantsEstimative?.totalEstimate}
              description='Média anual'
            />

            <StatisticsCard
              title='Atendimentos por Instrutor'
              iconClasses='fas fa-chart-line'
              statistic={participantsEstimative?.averagePerInstructor}
              description='Média anual'
            />
          </div>
          <div className='charts-graphics-container'>
            <div className='charts-card'>
              <div className='charts-card-title'>
                <p>Atendimentos no mês</p>
              </div>
              {lineChartData && (
                <Line
                  data={lineChartData}
                  options={lineChartOptions}
                />
              )}
            </div>
            <div className='charts-card'>
              <div className='charts-card-title'>
                <p>Atendimentos por média</p>
              </div>

              {pieChartData && (
                <Pie
                  data={pieChartData}
                  options={pieChartOptions}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard
